import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const device = z
  .object({
    appVersion: z.string().max(32).optional(),
    osVersion: z.string().max(32).optional(),
    deviceModel: z.string().max(64).optional(),
    locale: z.string().max(16).optional(),
    timezone: z.string().max(64).optional(),
    screenWidth: z.number().int().min(0).max(20000).optional(),
    screenHeight: z.number().int().min(0).max(20000).optional(),
    density: z.number().min(0).max(10).optional(),
    cores: z.number().int().min(0).max(256).optional(),
  })
  .optional();

const appEventSchema = z.object({
  eventName: z.enum(["APP_OPEN", "SIGN_UP", "CONSENT_UPDATE"]),
  platform: z.enum(["ios", "android"]),
  installId: z.string().min(8).max(64).regex(/^[\w-]+$/),
  trackingAllowed: z.boolean(),
  signUpMethod: z.string().max(16).optional(),
  device,
});

/**
 * Called by the iOS/Android app (WebView) for app lifecycle events.
 * SIGN_UP is only accepted for a signed-in account created within 24h,
 * and is deduped per account. START_TRIAL / PURCHASE are never accepted
 * from the client — they come only from server-verified billing.
 */
export const trackSnapAppEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => appEventSchema.parse(d))
  .handler(async ({ data, context }) => {
    const { sendSnapEvent } = await import("@/lib/snap-capi.server");
    const { data: auth, error: authError } = await context.supabase.auth.getUser();
    const user = auth.user;
    if (authError || !user) return { status: "skipped" };
    const now = Date.now();
    let eventId: string;
    if (data.eventName === "CONSENT_UPDATE") eventId = "";
    else if (data.eventName === "APP_OPEN") eventId = `open_${user.id}_${data.platform}_${data.installId}_${Math.floor(now / 60000)}`;
    else {
      if (!user) return { status: "skipped" };
      const created = new Date(user.created_at).getTime();
      if (!user.email_confirmed_at || !Number.isFinite(created) || created > now || now - created > 300_000) return { status: "skipped" };
      eventId = `signup_${user.id}`;
    }
    if (data.eventName === "CONSENT_UPDATE") return { status: "skipped" };
    if (user) {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      await supabaseAdmin.from("snap_app_devices").upsert({
        user_id: user.id,
        platform: data.platform,
        tracking_allowed: data.trackingAllowed,
        updated_at: new Date().toISOString(),
      });
    }
    const fwd = getRequestHeader("cf-connecting-ip") ?? getRequestHeader("x-forwarded-for")?.split(",")[0]?.trim();
    const r = await sendSnapEvent({
      eventName: data.eventName,
      eventId,
      platform: data.platform,
      trackingAllowed: data.trackingAllowed,
      userId: user?.id ?? null,
      email: user?.email ?? null,
      clientIp: fwd ?? null,
      userAgent: getRequestHeader("user-agent") ?? null,
      device: data.device,
      signUpMethod: data.eventName === "SIGN_UP" ? data.signUpMethod : undefined,
      origin: "app",
      eventTimeMs: data.eventName === "SIGN_UP" ? Date.parse(user.created_at) : now,
    });
    return { status: r.status };
  });

async function assertSuperAdmin(ctx: { supabase: any; userId: string }) {
  const { data } = await ctx.supabase.rpc("has_role", { _user_id: ctx.userId, _role: "super_admin" });
  if (!data) throw new Error("Forbidden");
}

export const listSnapEvents = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertSuperAdmin(context);
    const { data, error } = await context.supabase
      .from("snap_capi_events")
      .select("id, event_name, platform, origin, status, http_status, attempts, error, is_test, created_at")
      .order("created_at", { ascending: false })
      .limit(100);
    if (error) throw new Error("Could not load events");
    // Summaries describe evidence in logs, not proof of campaign attribution.
    const safe = (data ?? []).map((e) => ({ ...e, error: e.error && !/^(tracking not permitted by user|token not configured|Snapchat VALID|Snapchat response not confirmed|Provider validation error \(details withheld\)|No app id provided for app event|Insufficient user data|Invalid event time|Invalid action source)$/.test(e.error) ? "Provider error (details withheld)" : e.error }));
    return { events: safe, tokenConfigured: !!process.env["SNAPCHAT_CAPI_TOKEN"] };
  });

export const sendSnapTestEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      eventName: z.enum(["APP_INSTALL", "APP_OPEN", "SIGN_UP", "START_TRIAL", "PURCHASE"]),
      platform: z.enum(["ios", "android"]),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertSuperAdmin(context);
    if (!import.meta.env.DEV) throw new Error("Validation is available in development only");
    const { sendSnapEvent } = await import("@/lib/snap-capi.server");
    const r = await sendSnapEvent({
      eventName: data.eventName,
      eventId: `test_${data.eventName}_${crypto.randomUUID()}`,
      platform: data.platform,
      trackingAllowed: true,
      email: "snap-capi-test@inquira.online",
      device: { appVersion: "1", osVersion: "17", deviceModel: "ValidationDevice", locale: "en_US", screenWidth: 1080, screenHeight: 1920, density: 2, cores: 4, timezone: "UTC" },
      userAgent: "InquiraSnapTest/1.0",
      clientIp: "203.0.113.10",
      value: data.eventName === "PURCHASE" ? 9.99 : undefined,
      currency: data.eventName === "PURCHASE" ? "USD" : undefined,
      origin: "admin_test",
      validateOnly: true,
    });
    return r;
  });
